/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Typography,
  Grid,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    borderRadius: 16,
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.05)",
    backgroundColor: "#ffffff",
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
    },
  },
  progressSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  progressLabel: {
    color: "#555555",
    fontSize: "1.1rem",
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  moduleCount: {
    fontSize: "1.1rem",
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  moduleCompleted: {
    color: "#555555",
    fontSize: "1rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9rem",
    },
  },
  circularProgressContainer: {
    position: "relative",
    width: 100,
    height: 100,
    marginRight: theme.spacing(3),
    display: "inline-flex",
    [theme.breakpoints.down("xs")]: {
      width: 80,
      height: 80,
      marginRight: theme.spacing(2),
    },
  },
  circularProgressBackground: {
    color: "#f0f0f0",
    position: "absolute",
    top: 0,
    left: 0,
  },
  circularProgress: {
    color: "#36b37e",
    position: "absolute",
    top: 0,
    left: 0,
  },
  circularProgressLabel: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  percentage: {
    fontWeight: 700,
    fontSize: "1.8rem",
    color: "#333333",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.5rem",
    },
  },
  statusCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5),
    },
  },
  completedCard: {
    backgroundColor: "rgba(54, 179, 126, 0.1)",
  },
  failedCard: {
    backgroundColor: "rgba(225, 49, 47, 0.1)",
  },
  notStartedCard: {
    backgroundColor: "rgba(240, 240, 240, 0.5)",
  },
  statusIcon: {
    fontSize: 24,
    marginBottom: theme.spacing(1),
  },
  completedIcon: {
    color: "#36b37e",
  },
  failedIcon: {
    color: "#e1312f",
  },
  notStartedIcon: {
    color: "#6b778c",
  },
  statusNumber: {
    fontWeight: 700,
    fontSize: "1.5rem",
    marginBottom: theme.spacing(0.5),
  },
  completedNumber: {
    color: "#36b37e",
  },
  failedNumber: {
    color: "#e1312f",
  },
  notStartedNumber: {
    color: "#6b778c",
  },
  statusLabel: {
    color: "#555555",
    fontSize: "1rem",
    fontWeight: 500,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9rem",
    },
  },
  progressContent: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      "& > div:last-child": {
        marginTop: theme.spacing(2),
        textAlign: "center",
      },
    },
  },
  statusGrid: {
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(2),
    },
  },
  lastStatusCard: {
    [theme.breakpoints.down("xs")]: {
      marginBottom: 0,
    },
  },
}));

/**
 * Displays the user's progress in completing training modules.
 * The component shows overall progress as a percentage, and
 * displays the number of completed, failed, and not started
 * modules.
 *
 * @param {Array} modules - An array of training modules.
 * @returns {React.Element}
 */
function ProgressTracking({ modules }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const totalModules = modules.length;
  const completedModules = modules.filter(module =>
    module.training_user_attempt_index.some(attempt => attempt.pass)
  ).length;
  const failedModules = modules.filter(
    module =>
      module.training_user_attempt_index.length > 0 &&
      !module.training_user_attempt_index.some(attempt => attempt.pass)
  ).length;
  const notStartedModules = totalModules - completedModules - failedModules;

  const progressPercentage =
    totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

  const classes = useStyles({ progressPercentage });

  return (
    <Paper className={classes.root} elevation={0}>
      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12} className={classes.progressSection}>
          <Typography className={classes.progressLabel}>
            Overall Progress
          </Typography>
          <div className={classes.progressContent}>
            <div className={classes.circularProgressContainer}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={isMobile ? 80 : 100}
                thickness={4}
                className={classes.circularProgressBackground}
              />
              <CircularProgress
                variant="determinate"
                value={progressPercentage}
                size={isMobile ? 80 : 100}
                thickness={4}
                className={classes.circularProgress}
              />
              <div className={classes.circularProgressLabel}>
                <Typography className={classes.percentage}>
                  {progressPercentage}%
                </Typography>
              </div>
            </div>
            <div>
              <Typography className={classes.moduleCount}>
                {totalModules} Total Modules
              </Typography>
              <Typography className={classes.moduleCompleted}>
                {completedModules} of {totalModules} completed
              </Typography>
            </div>
          </div>
        </Grid>

        <Grid item xs={12} className={classes.statusGrid}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                className={`${classes.statusCard} ${classes.completedCard}`}
              >
                <CheckCircleIcon
                  className={`${classes.statusIcon} ${classes.completedIcon}`}
                />
                <Typography
                  className={`${classes.statusNumber} ${classes.completedNumber}`}
                >
                  {completedModules}
                </Typography>
                <Typography className={classes.statusLabel}>
                  Completed
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                className={`${classes.statusCard} ${classes.failedCard}`}
              >
                <ErrorIcon
                  className={`${classes.statusIcon} ${classes.failedIcon}`}
                />
                <Typography
                  className={`${classes.statusNumber} ${classes.failedNumber}`}
                >
                  {failedModules}
                </Typography>
                <Typography className={classes.statusLabel}>Failed</Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper
                elevation={0}
                className={`${classes.statusCard} ${classes.notStartedCard} ${
                  isMobile ? classes.lastStatusCard : ""
                }`}
              >
                <PlayArrowIcon
                  className={`${classes.statusIcon} ${classes.notStartedIcon}`}
                />
                <Typography
                  className={`${classes.statusNumber} ${classes.notStartedNumber}`}
                >
                  {notStartedModules}
                </Typography>
                <Typography className={classes.statusLabel}>
                  Not Started
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ProgressTracking;
